import Vue from "vue";
import Router from "vue-router";
import i18n from "@/plugins/i18n_single_language_file";
import store from "../store";
import { Role, AllRoles, AdminRoles } from "@/helper/roles";
import manufacturerRoutes from "./manufacturer";
import supplierRoutes from "./supplier";
import restaurantRoutes from "./restaurant";

Vue.use(Router);

const backNavigationState = Vue.observable({
  previousRoute: null,
});

const BACK_NAVIGATION_DISABLED_ROUTES = ["Login", "Register", "PasswordReset"];

const cloneBackRoute = (route) => {
  if (!route) return null;

  return {
    name: route.name || null,
    path: route.path || null,
    fullPath: route.fullPath || null,
    params: route.params || {},
    query: route.query || {},
  };
};

const isBackNavigationCandidate = (route) => {
  return Boolean(
    route &&
    route.fullPath &&
    !BACK_NAVIGATION_DISABLED_ROUTES.includes(route.name)
  );
};

// Silence known benign navigation errors (redirects, cancellations, duplicates)
const originalPush = Router.prototype.push;
Router.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject);
  return originalPush.call(this, location).catch((err) => {
    if (
      err && (
        err.name === "NavigationDuplicated" ||
        err.name === "NavigationCancelled" ||
        err.name === "NavigationAborted" ||
        err.name === "NavigationRedirected" ||
        (typeof err.message === "string" && err.message.indexOf("Redirected when going from") !== -1)
      )
    ) {
      return err;
    }
    return Promise.reject(err);
  });
};

const originalReplace = Router.prototype.replace;
Router.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) return originalReplace.call(this, location, onResolve, onReject);
  return originalReplace.call(this, location).catch((err) => {
    if (
      err && (
        err.name === "NavigationDuplicated" ||
        err.name === "NavigationCancelled" ||
        err.name === "NavigationAborted" ||
        err.name === "NavigationRedirected" ||
        (typeof err.message === "string" && err.message.indexOf("Redirected when going from") !== -1)
      )
    ) {
      return err;
    }
    return Promise.reject(err);
  });
};

const router = new Router({
  mode: "history", // https://router.vuejs.org/api/#mode
  linkActiveClass: "open active",
  scrollBehavior: () => ({ y: 0 }),
  routes: [
    {
      path: "/login",
      name: "Login",
      component: () => import("@/views/Auth/Login"),
    },
    {
      path: "/register",
      name: "Register",
      // beforeEnter: guest,
      component: () => import("@/views/Auth/Register-wizard"),
    },
    {
      path: "/password-reset/:token",
      name: "PasswordReset",
      component: () => import("@/views/Auth/Login"),
    },

    // General routes
    {
      path: "/",
      redirect: "/dashboard",
      name: "Home",
      meta: { translated: true, label: "➜" },
      // beforeEnter: auth,
      component: () => import("@/containers/TheContainer"),

      children: [
        {
          path: "dashboard/:event?",
          name: "Dashboard",
          meta: { translated: false, label: "sidebar.dashboard", authorize: AllRoles },
          component: () => import("@/views/Dashboard"),
        },
        {
          path: "403",
          meta: { label: "HTTP 403 Forbidden", authorize: AllRoles },
          name: "Page403",
          component: () => import("@/views/Auth/Page403"),
        },
        {
          path: "404",
          meta: { label: "Page Not Found", authorize: AllRoles },
          name: "Page404",
          component: () => import("@/views/Auth/Page404"),
        },
        {
          path: "profile",
          name: "Profile",
          meta: { translated: false, label: "user.edit_profile", authorize: AllRoles },
          component: () => import("@/views/profile/Index"),
        },

        //#region Users
        {
          path: "users",
          meta: { translated: false, label: "user.users", authorize: [Role.SuperAdmin] },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              name: "Users",
              meta: { translated: false, label: "user.all_users", authorize: [Role.SuperAdmin] },
              component: () => import("@/views/users/List"),
            },
            {
              path: "add",
              name: "Add New User",
              meta: { translated: false, label: "user.add_new_user", authorize: [Role.SuperAdmin] },
              component: () => import("@/views/users/Item"),
            },
            {
              path: ":id",
              name: "Edit User",
              meta: { translated: false, label: "user.edit_user", authorize: [Role.SuperAdmin] },
              component: () => import("@/views/users/Item"),
            },
          ],
        },
        //#endregion

        //#region Firms
        {
          path: "firms",
          redirect: "/dashboard",
          meta: { translated: false, label: "firm.firms", authorize: AdminRoles },
          component: { render(c) { return c("router-view"); } },
          children: [
            {
              path: ":id",
              name: "Edit Firm",
              meta: { translated: false, label: "firm.edit_firm", authorize: AdminRoles },
              component: () => import("@/views/firms/Item"),
            },
          ],
        },
        //#endregion

        //#region Suppliers
        {
          path: "suppliers",
          redirect: "/dashboard",
          meta: { translated: false, label: "global.suppliers", authorize: AllRoles },
          component: { render(c) { return c("router-view"); } },
          children: [
            {
              path: "",
              name: "Suppliers",
              meta: { translated: false, label: "global.suppliers", authorize: AllRoles },
              component: () => import("@/views/firms/suppliers/List"),
            },
          ],
        },
        //#endregion

        //#region Product Groups (Super Admin)
        {
          path: "product-groups",
          name: "PGroups",
          meta: {
            translated: false,
            label: "pgroup.product_groups",
            authorize: [Role.SuperAdmin],
          },
          component: () => import("@/views/pgroups/List"),
        },
        //#endregion

        //#region Health Checks
        {
          path: "health-checks",
          name: "Health Checks",
          meta: { translated: false, label: "health_checks.title", authorize: AdminRoles },
          component: () => import("@/views/health-checks/List"),
        },
        //#endregion

        //#region Atrify
        {
          path: "atrify",
          meta: { translated: false, label: "atrify.atrify", authorize: [Role.SuperAdmin] },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "items",
              name: "AtrifyItems",
              meta: { translated: false, label: "atrify.atrify_items", authorize: [Role.SuperAdmin] },
              component: () => import("@/views/atrify/List"),
            },
          ],
        },
        //#endregion

        // Import modular routes
        ...manufacturerRoutes,
        ...supplierRoutes,
        ...restaurantRoutes,
      ],
    },
    // Catch all route for 404
    {
      path: "*",
      redirect: "/404",
    },
  ],
});

router.appBackState = backNavigationState;

router.afterEach((to, from) => {
  if (!isBackNavigationCandidate(to)) {
    backNavigationState.previousRoute = null;
    return;
  }

  if (
    isBackNavigationCandidate(from) &&
    from.fullPath !== to.fullPath
  ) {
    backNavigationState.previousRoute = cloneBackRoute(from);
  }
});

// Filter out benign guard redirection errors from console
router.onError((error) => {
  if (
    error &&
    typeof error.message === "string" &&
    error.message.indexOf("Redirected when going from") !== -1
  ) {
    return;
  }
  // Log other errors
  // eslint-disable-next-line no-console
  console.error(error);
});

const translateRouteLabel = (routeRecord) => {
  if (!routeRecord || !routeRecord.meta) return;
  if (typeof routeRecord.meta.label !== "string") return;

  const originalLabel = routeRecord.meta.__rawLabel || routeRecord.meta.label;
  routeRecord.meta.__rawLabel = originalLabel;
  routeRecord.meta.label = originalLabel.includes(".") ? i18n.t(originalLabel) : originalLabel;
};

router.beforeEach((to, from, next) => {
  translateRouteLabel(to);
  to.matched.forEach((parentRoute) => translateRouteLabel(parentRoute));

  const isAuthenticated = store.getters["auth/isAuthenticated"];
  const currentSelectedFirm = store.state.selectedFirm;

  // Routes an authenticated user can visit without a selected firm.
  const routesAllowedWithoutFirmIfAuthenticated = [
    "Dashboard",
    "Page403",
    "Page404",
    "Profile",
    "Users",
    "Add New User",
    "Edit User",
    "Edit Firm",
    // "AtrifyItems",
  ];

  if (isAuthenticated) {
    if (
      !currentSelectedFirm &&
      !routesAllowedWithoutFirmIfAuthenticated.includes(to.name)
    ) {
      if (from.name === "Login" && to.query.from) {
        next({ path: to.query.from });
        return;
      }
      next({ name: "Dashboard", params: { event: "change-location" }, replace: true });
      return;
    }

    // Check firm type based routing
    if (currentSelectedFirm) {
      const firmType = currentSelectedFirm.type;
      const path = to.path;

      // Check if the route is under Home route
      const isUnderHome = to.matched.some((route) => route.name === "Home");

      // Only apply firm type redirection if it's not under Home
      if (!isUnderHome) {
        // Redirect based on firm type
        if (firmType === "M" && !path.startsWith("/manufacturer")) {
          next({ path: `/manufacturer${path}`, replace: true });
          return;
        } else if (firmType === "S" && !path.startsWith("/supplier")) {
          next({ path: `/supplier${path}`, replace: true });
          return;
        } else if (firmType === "R" && !path.startsWith("/restaurant")) {
          next({ path: `/restaurant${path}`, replace: true });
          return;
        }
      }

      // Role-based access control using authorize meta
      const userRoles = store.state.auth.userData?.roles || [];
      const roleNames = userRoles.map
        ? userRoles.map((r) => (typeof r === "string" ? r : r.name))
        : userRoles;

      // Check authorize meta on matched routes
      const authorizedRoles = to.meta?.authorize;
      if (authorizedRoles && Array.isArray(authorizedRoles)) {
        const hasAccess = authorizedRoles.some((role) =>
          roleNames.includes(role)
        );
        if (!hasAccess) {
          next({ name: "Page403", replace: true });
          return;
        }
      }
    }

    next();
  } else {
    if (["Login", "Register", "PasswordReset"].includes(to.name)) {
      next();
    } else {
      const loginpath = window.location.pathname;
      const searchParams = new URL(window.location.href).searchParams;
      const HappyGastAuth = searchParams.get("HappyGastAuth");
      const HappyGastLokation = searchParams.get("HappyGastLokation");
      let query = { from: loginpath };

      if (HappyGastAuth) {
        query.HappyGastAuth = HappyGastAuth;
        query.HappyGastLokation = HappyGastLokation;
      }
      next({ name: "Login", query: query, replace: true });
    }
  }
});

export default router;
